import { useState } from 'react';

import { Input } from '@/components/ui/input';
import { usePageTitle } from '@/hooks/use-page-title';
import { ApiRequestError } from '@/lib/api';
import { UserAvatar } from '@/platform/components/user-avatar';
import { PlatformPageHeader } from '@/platform/components/platform-page-header';
import { useVoiceCopy } from '@/platform/voice/use-voice';
import {
  leavePanelClassName,
  leaveTableFrameClassName,
} from '@/products/leave/features/leave-panel';
import { useLeaveColleagues } from '@/products/leave/features/use-leave';

export function LeaveTeamPage() {
  usePageTitle('Das Team');
  const copy = useVoiceCopy();
  const colleagues = useLeaveColleagues();
  const [query, setQuery] = useState('');
  const needle = query.trim().toLowerCase();
  const items = (colleagues.data?.items ?? []).filter((person) => {
    if (!needle) {
      return true;
    }
    return [person.displayName, person.departmentName, person.siteName, person.phone]
      .filter((value): value is string => Boolean(value))
      .some((value) => value.toLowerCase().includes(needle));
  });

  return (
    <div className="space-y-6">
      <PlatformPageHeader title={copy('leave.colleagues.title')} description="Das Team" />
      {colleagues.isError ? (
        <p className="text-sm text-destructive">
          {colleagues.error instanceof ApiRequestError
            ? colleagues.error.message
            : 'Das Team konnte nicht geladen werden.'}
        </p>
      ) : (
        <>
          <div className={leavePanelClassName}>
            <Input
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              placeholder="Nach Name, Abteilung oder Standort filtern"
              aria-label="Nach Name, Abteilung oder Standort filtern"
            />
          </div>
          {colleagues.isPending ? (
            <p className="text-sm text-muted-foreground">Team wird geladen …</p>
          ) : null}
          {colleagues.data?.items.length === 0 ? (
            <p className="text-sm text-muted-foreground">
              {copy('leave.calendar.department-empty')}
            </p>
          ) : null}
          {(colleagues.data?.items.length ?? 0) > 0 && items.length === 0 ? (
            <p className="text-sm text-muted-foreground">Keine Person gefunden.</p>
          ) : null}
          {items.length > 0 ? (
            <div className={leaveTableFrameClassName}>
              <table className="w-full min-w-[40rem] text-sm">
                <thead className="bg-muted/40 text-left">
                  <tr>
                    <th className="px-3 py-2 font-medium">Name</th>
                    <th className="px-3 py-2 font-medium">Abteilung</th>
                    <th className="px-3 py-2 font-medium">Standort</th>
                    <th className="px-3 py-2 font-medium">Telefon</th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((person) => (
                    <tr key={person.id} className="border-t">
                      <td className="px-3 py-2">
                        <span className="flex items-center gap-2">
                          <span className="inline-flex size-8 shrink-0">
                            <UserAvatar
                              firstName={person.firstName}
                              lastName={person.lastName}
                              displayName={person.displayName}
                              profilePhotoUrl={person.profilePhotoUrl}
                            />
                          </span>
                          <span className="font-medium">{person.displayName}</span>
                        </span>
                      </td>
                      <td className="px-3 py-2">{person.departmentName || '–'}</td>
                      <td className="px-3 py-2">{person.siteName ?? '–'}</td>
                      <td className="px-3 py-2">{person.phone ?? '–'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : null}
        </>
      )}
    </div>
  );
}
